<template>
  <main class="welcome">
    <div class="ambient" aria-hidden="true"></div>

    <section class="intro" aria-labelledby="welcome-title">
      <img class="mark" src="/static/aime-mark.svg" alt="AiMe" width="100" height="100" />

      <div class="identity">
        <h1 id="welcome-title">AiMe</h1>
        <p class="eyebrow">AI-NATIVE SOLANA PLATFORM</p>
      </div>

      <h2>Build on Solana.<br /><span>From idea to app.</span></h2>

      <p class="description">
        Create, run and automate Solana apps with AI.
        Games, trading tools, scanners and more — built with your agents.
      </p>

      <button class="enter" type="button" @click="login" :disabled="isLoading">
        <span>{{ isLoading ? 'Connecting...' : 'Enter with Phantom' }}</span>
        <svg v-if="!isLoading" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M5 12h14m-6-6 6 6-6 6" stroke="currentColor" stroke-width="1.8"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>

      <p v-if="error" class="error" role="alert">{{ error }}</p>
      <p class="note">Your wallet is your identity.</p>
      <p class="motto">Complexity belongs to the platform.<br /><strong>Simplicity belongs to you.</strong></p>
    </section>
  </main>
</template>

<script setup>
import { ref } from 'vue'
import { useStorage } from '@/stores'

const store = useStorage()

defineOptions({ name: 'WelcomeView' })

const isLoading = ref(false)
const error = ref('')

function getPhantomProvider() {
  const anyWindow = window

  if (anyWindow.phantom?.solana?.isPhantom) {
    return anyWindow.phantom.solana
  }

  if (anyWindow.solana?.isPhantom) {
    return anyWindow.solana
  }

  return null
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent || '')
}

function openInPhantom() {
  const dappUrl = encodeURIComponent(window.location.href)
  const ref = encodeURIComponent(window.location.origin)
  window.location.href = `https://phantom.app/ul/browse/${dappUrl}?ref=${ref}`
}

async function login() {
  error.value = ''

  const provider = getPhantomProvider()

  if (!provider) {
    if (isIOS()) {
      openInPhantom()
    } else {
      error.value = 'Phantom wallet not found. Please install Phantom and refresh the page.'
    }
    return
  }

  isLoading.value = true

  try {
    const resp = await provider.connect()
    const publicKey = resp.publicKey.toString()

    const message = `Login to AiMe\nTimestamp: ${Date.now()}`
    const encodedMessage = new TextEncoder().encode(message)

    const signedMessage = await provider.signMessage(encodedMessage, 'utf8')
    const signature = Array.from(signedMessage.signature)
      .map(b => String.fromCharCode(b))
      .join('')
    const signatureBase64 = btoa(signature)

    await store.login(publicKey, message, signatureBase64)
    window.location.replace('/')
  } catch (err) {
    console.error('Login error:', err)

    const msg = err?.message || String(err || '')

    if (msg.includes('User rejected')) {
      error.value = 'Connection cancelled'
    } else if (msg.includes('Unauthorized wallet')) {
      error.value = 'This wallet is not authorized'
    } else {
      error.value = msg || 'Failed to connect wallet'
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.welcome {
  --text: #f5f4fa;
  --muted: #a3a4b6;
  --accent: #b8a6ff;
  position: relative;
  flex: 1;
  display: grid;
  place-items: center;
  min-height: 100svh;
  padding: clamp(24px, 5vh, 52px) 22px;
  isolation: isolate;
  overflow: hidden;
  color: var(--text);
  background: #09090e;
}

.welcome::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -2;
  background:
    radial-gradient(ellipse 45% 36% at 50% 37%, rgba(111, 75, 204, .18), transparent 85%),
    radial-gradient(ellipse 45% 40% at 80% 100%, rgba(66, 92, 156, .08), transparent 80%),
    #09090e;
}

.ambient {
  position: absolute;
  z-index: -1;
  top: 17%;
  left: 50%;
  width: min(88vw, 640px);
  aspect-ratio: 1;
  transform: translateX(-50%);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(146, 103, 247, .065), transparent 68%);
  filter: blur(28px);
  pointer-events: none;
}

.intro {
  width: min(100%, 620px);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.mark {
  width: clamp(66px, 10vh, 100px);
  height: clamp(66px, 10vh, 100px);
  object-fit: contain;
  filter: drop-shadow(0 14px 28px rgba(135, 94, 255, .18));
}

.identity {
  margin-top: 13px;
}

.identity h1 {
  margin: 0;
  font-size: clamp(31px, 4.5vh, 42px);
  font-weight: 750;
  line-height: 1;
  letter-spacing: -.055em;
}

.eyebrow {
  margin-top: 10px;
  color: #8a88a9;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .22em;
}

h2 {
  margin-top: clamp(26px, 5vh, 46px);
  font-size: clamp(34px, 5.5vw, 60px);
  font-weight: 720;
  line-height: 1.06;
  letter-spacing: -.055em;
}

h2 span {
  background: linear-gradient(100deg, #dcd1ff, #ad9afa 55%, #83c0ec);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
}

.description {
  max-width: 490px;
  margin-top: 18px;
  font-size: clamp(14px, 1.55vw, 16px);
  line-height: 1.65;
  color: var(--muted);
  text-wrap: pretty;
}

.enter {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 13px;
  min-width: 220px;
  min-height: 52px;
  margin-top: 32px;
  padding: 0 24px;
  background: #ded6ff;
  border: 1px solid rgba(255, 255, 255, .4);
  border-radius: 12px;
  color: #141022;
  font: inherit;
  font-size: 14px;
  font-weight: 750;
  cursor: pointer;
  box-shadow: 0 12px 42px rgba(114, 83, 221, .16);
  transition: transform .2s ease, box-shadow .2s ease, background .2s ease;
}

.enter:hover:not(:disabled) {
  transform: translateY(-2px);
  background: #eee9ff;
  box-shadow: 0 16px 46px rgba(114, 83, 221, .26);
}

.enter:focus-visible {
  outline: 3px solid #a894ff;
  outline-offset: 4px;
}

.enter:disabled {
  opacity: .65;
  cursor: wait;
}

.enter svg {
  width: 18px;
  height: 18px;
}

.error {
  margin-top: 14px;
  max-width: 450px;
  color: #ff9da6;
  font-size: 13px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.note {
  margin-top: 14px;
  font-size: 12px;
  color: #77788b;
}

.motto {
  margin-top: clamp(24px, 4.5vh, 42px);
  font-size: 12px;
  line-height: 1.65;
  color: #77788b;
}

.motto strong {
  font-weight: 600;
  color: #b3abc7;
}

@media (max-width: 560px) {
  .welcome { padding: 24px 22px max(24px, env(safe-area-inset-bottom)); }
  .description { max-width: 350px; margin-top: 15px; }
  h2 { margin-top: 28px; font-size: clamp(34px, 9vw, 46px); }
  .enter { margin-top: 25px; width: min(100%, 280px); }
  .motto { margin-top: 26px; }
}

@media (max-height: 640px) {
  .welcome { padding-top: 18px; padding-bottom: 18px; }
  .mark { width: 56px; height: 56px; }
  h2 { margin-top: 18px; }
  .description { margin-top: 11px; }
  .enter { margin-top: 17px; min-height: 46px; }
  .motto { margin-top: 15px; }
}

@media (prefers-reduced-motion: reduce) {
  .enter { transition: none; }
  .enter:hover:not(:disabled) { transform: none; }
}
</style>
